import { Box, Button, Dialog } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ModalV2Actions from '/@/components/bayer/modal-actions';
import ModalV2Header from '/@/components/bayer/modal-header';
import MapComponent from '/@/components/map';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';

/**
 * Full-screen (mobile only — desktop keeps the side-by-side map) "Ver en mapa" picker: tap a
 * field on the map, then confirm with "Seleccionar". Mirrors the Figma "Mapa" modal.
 *
 * Uses a plain `Dialog fullScreen` (like `HumidityMapModal`) rather than `ModalV2`: `ModalV2`'s
 * `Container` is hard-coded for a small centered dialog (`position: absolute; top/left: 50%`,
 * no height), which isn't a sound foundation for a true full-screen layout — the Google Maps
 * canvas div (`position: absolute; inset: 0`) needs a concrete-height ancestor chain to have
 * any size at all, and `Dialog`'s `fullScreen` prop is MUI's own tested mechanism for that.
 */
function FieldSelectMapModal({
    open,
    onClose,
    fieldList,
    country,
    onSelect,
}: {
    open: boolean;
    onClose: () => void;
    fieldList: Field[];
    country?: string;
    onSelect: (field: Field) => void;
}) {
    const mapRef = useRef<{ focusMap?: () => void } | null>(null);
    const [pendingField, setPendingField] = useState<Field | undefined>(undefined);

    useEffect(() => {
        if (!open) return;
        // Defer until the fullScreen Dialog has finished opening and the map has laid out /
        // gone idle. Calling focusMap() synchronously on `open` runs fitBounds before the map
        // is ready, so it's a no-op and the map is left with no center/zoom (gray until a later
        // setCenter, e.g. the device-location button). Mirrors HumidityMapModal.
        const timer = setTimeout(() => mapRef.current?.focusMap?.(), 150);
        return () => clearTimeout(timer);
    }, [open, fieldList]);

    const handleClose = () => {
        setPendingField(undefined);
        onClose();
    };

    const handleConfirm = () => {
        if (!pendingField) return;
        onSelect(pendingField);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullScreen>
            <ModalV2Header onClose={handleClose}>{_('Mapa')}</ModalV2Header>
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <MapComponent
                    ref={mapRef}
                    fields={fieldList}
                    clusterMarkers
                    country={country || 'AR'}
                    offline={false}
                    simplifyPolygons
                    onFieldClick={(clickedField) => setPendingField(clickedField)}
                    selectedField={pendingField}
                />
            </Box>
            <ModalV2Actions>
                <Button fullWidth variant='contained' color='primary' disabled={!pendingField} onClick={handleConfirm}>
                    {_('Seleccionar')}
                </Button>
            </ModalV2Actions>
        </Dialog>
    );
}

export default FieldSelectMapModal;
